import { ArrayFieldTemplateProps } from "@rjsf/utils";
import React from "react";
import { Button, Collapse, Form } from "antd";
import { DeleteOutlined, PlusCircleOutlined } from "@ant-design/icons";

// Helper function to determine if an array item is single-level
const isSingleLevelItem = (item: any) =>
  typeof item.schema === "boolean" || item.schema.type !== "object";

// Helper function to render nested objects with indentation
const renderNestedObject = (props: any, level: number = 0) => {
  const indentStyle = { paddingLeft: `${level * 20}px` };

  return (
    <div style={indentStyle}>
      {Object.keys(props.properties).map((propertyName) => (
        <Form.Item
          className="sb-field"
          key={propertyName}
          label={propertyName}
          colon={false}
          labelAlign="left"
          labelCol={{ flex: '0 0 var(--sb-label-w)' }}
          wrapperCol={{ flex: '1 1 0' }}
        >
          {props.properties[propertyName].uiSchema["ui:widget"] ===
            "CustomArrayField" ? (
            <CustomArrayFieldTemplate
              {...props.properties[propertyName]}
              level={level + 1}
            />
          ) : (
            props.properties[propertyName].content
          )}
        </Form.Item>
      ))}
    </div>
  );
};

/**
 * An array field: its items, and the control that adds another.
 *
 * Each remove button used to be an unlabelled red circle — several to a form,
 * all reading the same to anything that could not see which row it sat on.
 * They say which item they remove now.
 */
const CustomArrayFieldTemplate = (
  props: ArrayFieldTemplateProps & { level?: number }
) => {
  const level = props.level || 0;
  const isSingleLevelArray = props.items && props.items.every(isSingleLevelItem);

  const removeLabel = (element: any) =>
    `Remove ${props?.title ? `${props.title} ` : ''}item ${element.index + 1}`;

  const RemoveButton = ({ element }: { element: any }) => (
    <Button
      danger
      type="primary"
      shape="circle"
      className="submission-builder-del-btn"
      icon={<DeleteOutlined />}
      aria-label={removeLabel(element)}
      title={removeLabel(element)}
      onClick={element.onDropIndexClick(element.index)}
    />
  );

  // Render function for single-level array items
  const renderSingleLevelItem = (element: any) => (
    <div className="sb-array__row" key={element.key}>
      {element.children}
      <RemoveButton element={element} />
    </div>
  );

  // Render function for nested array items
  const renderNestedItem = (element: any) => (
    <Collapse
      className="sb-array__item"
      key={element.key}
      bordered
      defaultActiveKey={[]}
      items={[{
        key: element.key,
        label: (
          <span className="sb-array__head">
            <span className="sb-array__head-name">
              {element?.children?.props?.name ?? `Item ${element.index + 1}`}
            </span>
          </span>
        ),
        extra: (
          /* inside the header but outside its toggle: removing an item is not
             a way of opening it */
          <span onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <RemoveButton element={element} />
          </span>
        ),
        children: element.hasChildren ? renderNestedObject(element, level) : element.children,
      }]}
    />
  );

  return (
    <div className="sb-array">
      {props.items &&
        props.items.map(isSingleLevelArray ? renderSingleLevelItem : renderNestedItem)}
      {props.canAdd && (
        <Button
          type="dashed"
          className="sb-array__add"
          onClick={props.onAddClick}
          icon={<PlusCircleOutlined />}
        >
          Add {props?.title}
        </Button>
      )}
    </div>
  );
};

export default CustomArrayFieldTemplate;
